import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { AppDataGrid, type AppColumn } from "../../components/AppDataGrid";
import { AppDialog } from "../../components/AppDialog";
import { ExportMenu } from "../../components/AppToolbar";
import { useConfirm } from "../../components/confirm";
import { GridSearch } from "../../components/GridSearch";
import { useHotkeys, formatCombo } from "../../components/hotkeys";
import { fuzzyMatch } from "../../utils/fuzzyMatch";
import { useNotify } from "../../components/notifications";

/**
 * Settings → Test Code → **Group Test** — what a test with `Is Group` ticked
 * carries INSTEAD of Method Defaults and Test Parameter Defaults.
 *
 * Ported from GoLive, not invented:
 *   • the grid    `Model.xafml:23141` — `GroupTestMethod_ListView_IsGroup`
 *   • its host    `Model.xafml:67464` — the "TestMethodIsGroup" item on
 *                 `TestMethod_DetailView`
 *   • the filter  `TestParameterViewControllercs.cs:401` — `[TestMethod.Oid] = <group>`
 *   • ADD/REMOVE  XAF's Link / Unlink, re-captioned in that controller
 *   • the picker  `Model.xafml:70624` — `Testparameter_ListView_Testmethod_IsGroup`,
 *                 the SAME four columns over `dbo.Testparameter`
 *
 * ⚠ The **Parameter** column is NOT the parameter's name. Legacy binds it to the
 * BO's `[NonPersistent] GroupTestParameter`, which reads "Customized" when the
 * member carries its own parameter selection and "Default" otherwise
 * (`GroupTestMethod.cs:98-119`) — which is why a group whose members measure
 * Sulfate, E. coli and TSS shows "Default" three times. Verified against BMP on
 * the live database.
 *
 * That cell is also the OPENER: legacy hangs an `ondblclick` on it
 * (`TestParameterViewControllercs.cs:4058`) that raises the popup customizing
 * WHICH of the member test's parameters the group takes (:4792, written to
 * `GroupTestMethod.GroupTestParameters` → the cell flips to "Customized"). Here
 * it is a real button, so one click — or Enter on the keyboard — opens the same
 * picker; the API decides what it lists and what it preselects, because that is
 * a business rule and not a screen detail (CLAUDE.md §1).
 */

type Member = {
  oid: string;
  testParameterOid?: string | null;
  test?: string | null;
  method?: string | null;
  component?: string | null;
  parameter?: string | null;
};
type Candidate = { oid: string; test?: string | null; method?: string | null;
                   component?: string | null; parameter?: string | null };

/** The four columns, in the legacy order. Shared by the grid and the picker, so
 *  the ADD dialog reads exactly like the grid it adds to. */
const COLS: AppColumn<Member>[] = [
  { field: "test", header: "Test", width: 220 },
  { field: "method", header: "Method", width: 180 },
  { field: "component", header: "Component", width: 220 },
  { field: "parameter", header: "Parameter", width: 190 },
];

/** The grid's own columns: the four above, with Parameter rendered as the button
 *  that opens the picker. The picker is per-ROW, so the handler is passed in
 *  rather than captured in a module-level constant. */
const gridCols = (open: (row: Member) => void): AppColumn<Member>[] =>
  COLS.map((c) =>
    c.field !== "parameter"
      ? c
      : {
          ...c,
          // Sorting, filtering and export keep reading the text, so the cell
          // being a button changes nothing about the data.
          text: (r) => r.parameter ?? "",
          render: (r) => (
            <button
              type="button"
              className="tc-gt-param"
              title="Choose which of this test's parameters the group takes"
              onClick={() => open(r)}
            >
              {r.parameter ?? "Default"}
            </button>
          ),
        },
  );

/** The message out of either error shape the API can send. */
const errText = (e: unknown, fallback: string) => {
  const err = (e as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  if (typeof err === "string" && err.trim()) return err;
  const msg = (err as { message?: string } | undefined)?.message;
  return msg && msg.trim() ? msg : fallback;
};

const EXPORT_ROWS = (rows: Array<Member | Candidate>) =>
  rows.map((r) => [r.test ?? "", r.method ?? "", r.component ?? "", r.parameter ?? ""]);

export function TestCodeGroupTest({ base, testName }: { base: string; testName?: string }) {
  const [rows, setRows] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [paramsFor, setParamsFor] = useState<Member | null>(null);
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const { confirm } = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${base}/test-code/group-tests`);
      setRows(Array.isArray(data?.data?.rows) ? data.data.rows : []);
      setSel(new Set());
    } catch {
      // One failing card must not take the detail page down; the shared axios
      // error handling already surfaced it.
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // load is stable for a given base; listing it would re-read on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const remove = async () => {
    const oids = [...sel].map(String);
    if (!oids.length) return;
    const ok = await confirm({
      title: "Remove",
      tone: "danger",
      message: `Remove ${oids.length} test${oids.length === 1 ? "" : "s"} from ${testName ?? "this group"}?`,
      confirmLabel: "Remove",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/test-code/group-tests/remove`, { oids });
      notify.success(`Removed ${data?.data?.affected ?? oids.length}.`);
      await load();
    } catch (e: unknown) {
      notify.error(errText(e, "Could not remove those rows."));
    } finally {
      setBusy(false);
    }
  };

  /* Standing rule 14: this bar is hand-rolled, so it registers its own keys —
     a bespoke action row must never leave them to whatever else is on the page.
     ⚠ Alt+D is the REASON this exists (owner report 2026-10-29): the detail
     page binds it to Delete-the-whole-test, and with a member ticked that is
     almost never what the key is being pressed for. The store takes the latest
     ENABLED registration, so gating on the selection makes Alt+D mean Remove
     exactly while a member is ticked and fall back to the page's Delete
     otherwise — the group test is still deletable by keyboard, just not while
     you are pointing at one of its members. */
  useHotkeys([
    { combo: "alt+n", label: "Add to group", group: "Group Test", order: 1,
      disabled: busy, run: () => setAddOpen(true) },
    { combo: "alt+d", label: "Remove from group", group: "Group Test", order: 2,
      disabled: busy || !sel.size, run: () => void remove() },
  ]);

  return (
    <div className="sd-section" id="tc-sec-group">
      <h3 className="sd-section-h">Group Test</h3>
      <div className="sd-child-bar">
        <button className="ca-btn" onClick={() => setAddOpen(true)} disabled={busy}
                title={`Add tests to this group (${formatCombo("alt+n")})`}><span className="tb-i tb-i-green">＋</span> Add</button>
        <button className="ca-btn" onClick={() => void remove()} disabled={busy || !sel.size}
                title={`Remove the ticked tests from this group (${formatCombo("alt+d")})`}><span className="tb-i tb-i-red">−</span> Remove</button>
        <ExportMenu
          filename="group-test"
          headers={COLS.map((c) => String(c.header))}
          rows={EXPORT_ROWS(rows)}
        />
      </div>
      <AppDataGrid
        rows={rows}
        loading={loading}
        columns={gridCols(setParamsFor)}
        fixedLayout
        selectable
        selectedIds={sel}
        onSelectionChange={(ids) => setSel(new Set(ids))}
        getRowId={(r, i) => String(r.oid ?? i)}
        emptyText="No tests in this group. Click ADD."
        maximizable
        exportFilename="group-test"
        alwaysShowFooter
      />

      {addOpen && (
        <GroupTestAddDialog
          base={base}
          onClose={() => setAddOpen(false)}
          onAdded={() => { setAddOpen(false); void load(); }}
        />
      )}

      {paramsFor && (
        <GroupTestParamsDialog
          url={`${base}/test-code/group-tests/${paramsFor.oid}/parameters`}
          testName={paramsFor.test}
          onClose={() => setParamsFor(null)}
          onSaved={() => { setParamsFor(null); void load(); }}
        />
      )}
    </div>
  );
}

type ParamRow = { oid: string; parameter?: string | null; sort?: number | null;
                  selected: boolean; retired: boolean };

/** The PARAMETER picker — legacy's "Testparameter" popup, reached from the
 *  Parameter cell. ONE dialog serves both places legacy opens it from
 *  (TestParameterViewControllercs.cs:4051 on the ADD picker, :4058 on the member
 *  grid): the only difference is where the rows come from and what OK does with
 *  them, so both arrive as props rather than being branched on inside.
 *
 *  ⚠ WHAT IS TICKED WHEN IT OPENS IS NOT A UI DEFAULT. A member that stores
 *  nothing takes EVERY parameter, so the popup opens with all of them ticked
 *  (legacy :3943) — and OK on the whole list stores nothing again, which is the
 *  same thing said the other way. Both readings live in the SP, so this dialog
 *  only renders `selected` and sends back what the user left ticked. */
function GroupTestParamsDialog({
  url, testName, onClose, onPicked, onSaved,
}: {
  url: string;
  testName?: string | null;
  onClose: () => void;
  /** ADD picker: keep the choice locally; the add carries it. */
  onPicked?: (oids: string[], all: boolean) => void;
  /** Member grid: store it now. */
  onSaved?: () => void;
}) {
  const [rows, setRows] = useState<ParamRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const { confirm } = useConfirm();

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get(url);
        const list: ParamRow[] = Array.isArray(data?.data) ? data.data : [];
        if (!alive) return;
        setRows(list);
        setSel(new Set(list.filter((r) => r.selected).map((r) => key(r.oid))));
      } catch {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [url]);

  const shown = useMemo(
    () => (q.trim() ? rows.filter((r) => fuzzyMatch(q, r.parameter ?? "")) : rows),
    [rows, q]);

  const save = async () => {
    // The search box hides rows; the SELECTION is the whole set, not the part on
    // screen, so a filtered view cannot silently drop what it is not showing.
    const oids = [...sel].map(String);
    const all = oids.length === 0 || oids.length === rows.length;

    // From the ADD picker the test is not in the group yet, so there is nothing
    // to save and nothing to confirm — the choice rides along with the add, as
    // it does in legacy.
    if (onPicked) { onPicked(oids, all); return; }

    const ok = await confirm({
      title: "Parameters",
      message: all
        ? `${testName ?? "This test"} will take ALL of its parameters (Default). Do you want to Save?`
        : `${testName ?? "This test"} will take ${oids.length} of its ${rows.length} parameters. Do you want to Save?`,
    });
    if (!ok) return;

    setBusy(true);
    try {
      const { data } = await api.put(url, { oids });
      notify.success(data?.data?.changed === false
        ? "No change — that selection was already stored."
        : "Saved successfully.");
      onSaved?.();
    } catch (e: unknown) {
      notify.error(errText(e, "Could not save that selection."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open
      title="Parameters"
      width={560}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          <button className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={busy || loading}>
            OK
          </button>
          <button className="ca-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        </>
      }
    >
      {/* Its own wrapper, not the Component dialog's: that one is a CELL of a
          50/50 form grid, so borrowing it left the box at half width with dead
          space beside it (owner report). Here the search owns the row. */}
      <div data-hotkeys-ignore className="tc-gt-search">
        <GridSearch value={q} onChange={setQ} placeholder="Search parameters…" />
      </div>
      <div className="tc-comp-list">
        <AppDataGrid<ParamRow>
          rows={shown}
          loading={loading}
          getRowId={(r) => key(r.oid)}
          columns={[{
            field: "parameter",
            header: "Parameter",
            // A retired row is here only because the stored selection still
            // names it; say so rather than letting it read as a normal choice.
            render: (r) => (
              <span className={r.retired ? "tc-gt-retired" : undefined}>
                {r.parameter ?? ""}{r.retired ? " (retired)" : ""}
              </span>
            ),
            text: (r) => r.parameter ?? "",
          }]}
          selectable
          selectedIds={sel}
          onSelectionChange={(ids) => setSel(new Set([...ids].map(String)))}
          defaultPageSize={20}
          exportFilename="group-test-parameters"
          emptyText={rows.length ? `No parameter matches “${q}”.` : "This test has no parameters to choose from."}
        />
      </div>
    </AppDialog>
  );
}

/** Ids are compared as STRINGS and lower-cased: SQL renders a uniqueidentifier
 *  uppercase while a serialised Guid arrives lowercase, and a selection that
 *  keyed on the case it happened to arrive in would silently miss. */
const key = (v: unknown) => String(v ?? "").toLowerCase();

/** ADD — legacy's re-captioned Link. The candidates are every live parameter row
 *  of another test the group does not already carry, which the API decides; the
 *  dialog only picks from them. */
function GroupTestAddDialog({
  base, onClose, onAdded,
}: { base: string; onClose: () => void; onAdded: () => void }) {
  const [rows, setRows] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [busy, setBusy] = useState(false);
  // Candidate Oid → the parameters the user chose for it. A candidate with no
  // entry is added on "Default", which is every parameter.
  const [picks, setPicks] = useState<Map<string, string[]>>(new Map());
  const [paramsFor, setParamsFor] = useState<Candidate | null>(null);
  const notify = useNotify();

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get(`${base}/test-code/group-tests/candidates`);
        if (alive) setRows(Array.isArray(data?.data) ? data.data : []);
      } catch {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [base]);

  const add = async () => {
    const oids = [...sel].map(String);
    if (!oids.length) return;
    setBusy(true);
    try {
      // Only the candidates actually being added carry a selection — a choice
      // made and then unticked must not travel.
      const selections = oids
        .filter((o) => picks.has(o))
        .map((o) => ({ oid: o, parameterOids: picks.get(o)! }));
      const { data } = await api.post(`${base}/test-code/group-tests`,
        selections.length ? { oids, selections } : { oids });
      const added = data?.data?.affected ?? oids.length;
      notify.success(`Added ${added} test${added === 1 ? "" : "s"}.`);
      onAdded();
    } catch (e: unknown) {
      notify.error(errText(e, "Could not add those rows."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open
      title="Add tests to this group"
      width={980}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          <button className="ca-btn ca-btn-solid" onClick={() => void add()} disabled={busy || !sel.size}>
            OK
          </button>
          <button className="ca-btn" onClick={onClose} disabled={busy}>Cancel</button>
        </>
      }
    >
      <AppDataGrid
        rows={rows}
        loading={loading}
        columns={candidateCols(setParamsFor, picks)}
        selectable
        selectedIds={sel}
        onSelectionChange={(ids) => setSel(new Set(ids))}
        getRowId={(r, i) => String(r.oid ?? i)}
        emptyText="No tests to add. A group can only add tests on its own Matrix that have a Sample Parameter and are not already in the group."
        maximizable
        exportFilename="group-test-candidates"
        alwaysShowFooter
      />

      {paramsFor && (
        <GroupTestParamsDialog
          url={`${base}/test-code/group-tests/candidates/${paramsFor.oid}/parameters`}
          testName={paramsFor.test}
          onClose={() => setParamsFor(null)}
          onPicked={(oids, all) => {
            // "All" is Default, and Default is the absence of a choice — holding
            // the full list instead would send a selection the API has to
            // recognise as meaning nothing.
            setPicks((prev) => {
              const next = new Map(prev);
              if (all) next.delete(key(paramsFor.oid));
              else next.set(key(paramsFor.oid), oids);
              return next;
            });
            // Choosing parameters for a row is also choosing the row.
            setSel((prev) => new Set(prev).add(key(paramsFor.oid)));
            setParamsFor(null);
          }}
        />
      )}
    </AppDialog>
  );
}

/** The ADD picker's columns: the four shared ones, with Parameter rendered as
 *  the same opener the member grid has. It shows "Customized" once a choice has
 *  been made for that row, which is what the member row will read after the add.
 */
const candidateCols = (
  open: (row: Candidate) => void,
  picks: Map<string, string[]>,
): AppColumn<Candidate>[] =>
  (COLS as unknown as AppColumn<Candidate>[]).map((c) =>
    c.field !== "parameter"
      ? c
      : {
          ...c,
          text: (r) => (picks.has(key(r.oid)) ? "Customized" : r.parameter ?? ""),
          render: (r) => (
            <button
              type="button"
              className="tc-gt-param"
              title="Choose which of this test's parameters the group takes"
              onClick={() => open(r)}
            >
              {picks.has(key(r.oid)) ? "Customized" : r.parameter ?? "Default"}
            </button>
          ),
        },
  );
